import styled from 'styled-components';
import { s } from '../PeriodStatsPage.styles';
import type { NPLTrendRow } from '../graph/NPL/NPLTrendAnalysisGraph';
import SaveAsImageButton from '../../../components/button/SaveAsImageButton';
import { useRef, useMemo } from 'react';
import { downloadAsExcel } from '../../../../utils/downloadAsExcel';
import NPLTrendsAnalysisGraph from '../graph/NPL/NPLTrendAnalysisGraph';

type Props = {
  region: string;
  usage: string;
  usageDetail?: string;
  court?: string;
  fiMain?: string;
  fiSub?: string;
  data: NPLTrendRow[];
  loading?: boolean;
  error?: string | null;
};

export default function NPLTrendsAnalysis({
  region,
  usage,
  usageDetail,
  court,
  fiMain,
  fiSub,
  data = [],
  loading = false,
  error = null,
}: Props) {
  const targetRef = useRef<HTMLDivElement>(null);

  // 엑셀 저장: 화면과 동일한 컬럼명으로 구성
  const handleDownloadExcel = () => {
    const rows = data.map((r) => ({
      기간: r.label,
      근저당액_원: r.mortgageAmount,
      청구금액_원: r.claimPrice,
      '낙찰률(%)': r.winRate,
      '낙찰가율(%)': r.winPriceRate,
      '담보가치율(%)': r.collateralValueRate,
      '회수율(%)': r.recoveryRate,
      '손실률(%)': r.lossRate,
      '회수부족율(%)': r.shortfallRate,
    }));
    downloadAsExcel(rows as any[], 'NPL_동향_분석_데이터');
  };

  return (
    <s.Wrap ref={targetRef}>
      <s.Header>
        <s.HeaderRow>
          <s.Img $bg="#FEEFFC">
            <img src="/assets/images/icon/pink_arrow.svg" alt="" />
          </s.Img>
          <s.ButtonDiv>
            <SaveAsImageButton
              targetRef={targetRef}
              fileName="NPL_동향_분석.png"
            />
            <s.ExcelButton onClick={handleDownloadExcel} aria-label="엑셀 저장">
              <img src="/assets/images/icon/download-black.svg" alt="" />
            </s.ExcelButton>
          </s.ButtonDiv>
        </s.HeaderRow>
        <s.TitleBox>
          <h3>NPL 동향 분석</h3>
          <p>경매 대상 NPL 근저당액/청구금액, 낙찰률, 낙찰가율,</p>
          <p> 담보가치율, 회수율, 손실률, 회수 부족율 추이</p>
          <s.SelectedOptionBox>
            <s.SelectedOption>
              {court && court !== 'all' ? court : `${region} 지역`}
            </s.SelectedOption>
            <s.SelectedOption>
              {usage === '전체'
                ? `${usage} 용도`
                : `${usage} ${usageDetail ? ` ${usageDetail} 용도` : ''}`}
            </s.SelectedOption>
            <s.SelectedOption>
              {fiMain === '전체'
                ? `${fiMain} 금융기관`
                : `${fiSub ? ` ${fiSub}` : ''}`}
            </s.SelectedOption>
          </s.SelectedOptionBox>
        </s.TitleBox>
      </s.Header>

      {loading ? (
        <LoaderWrap>
          <LoaderImg
            src="/assets/images/icon/loader.gif"
            alt=""
          />
        </LoaderWrap>
      ) : (
        <>
          {/* 1. 근저당액/청구금액 + 낙찰률/낙찰가율 */}
          <NPLTrendsAnalysisGraph data={data} view="amount" />

          {/* 2. 담보가치율/회수율/손실률/회수부족율 */}
          <NPLTrendsAnalysisGraph data={data} view="rates" />
        </>
      )}
    </s.Wrap>
  );
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 40px;
`;

const LoaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  width: 100%;
`;

const LoaderImg = styled.img`
  width: 24px;
  height: 24px;
`;
